import type { MemberInfo, OrganizationDetail } from '@viewer/protocol';
import { useState, type FormEvent } from 'react';
import { api, errorText } from '../../lib/api/client';
import { useApi } from '../../lib/api/useApi';
import styles from './org.module.css';

interface Props {
  org: OrganizationDetail;
  onChanged(): Promise<void>;
  onDeleted(): Promise<void>;
}

export function SettingsTab({ org, onChanged, onDeleted }: Props) {
  const members = useApi<MemberInfo[]>(`/api/orgs/${org.id}/members`);
  const [name, setName] = useState(org.name);
  const [newOwner, setNewOwner] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function run(action: () => Promise<unknown>, done: string) {
    setError(null);
    setSuccess(null);
    try {
      await action();
      setSuccess(done);
    } catch (err) {
      setError(errorText(err));
    }
  }

  function rename(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || name.trim() === org.name) return;
    void run(async () => {
      await api.patch(`/api/orgs/${org.id}`, { name: name.trim() });
      await onChanged();
    }, 'Nome atualizado.');
  }

  function setExternalAccess(allow: boolean) {
    if (!allow && !confirm('Desligar o acesso externo encerra agora as sessões externas em andamento. Continuar?')) return;
    void run(async () => {
      await api.patch(`/api/orgs/${org.id}`, { allowExternalAccess: allow });
      await onChanged();
    }, allow ? 'Acesso externo permitido.' : 'Acesso externo desligado.');
  }

  function transfer(e: FormEvent) {
    e.preventDefault();
    const target = members.data?.find((m) => m.userId === newOwner);
    if (!target) return;
    if (!confirm(`Transferir a propriedade de "${org.name}" para ${target.name}? Você passa a ser administrador e não poderá desfazer.`)) return;
    void run(async () => {
      await api.post(`/api/orgs/${org.id}/transfer`, { userId: newOwner });
      await onChanged();
    }, `Agora ${target.name} é o proprietário.`);
  }

  function remove() {
    const typed = prompt(`Isso apaga a empresa, grupos, permissões e convites. Os computadores deixam de ser gerenciados por ela.\n\nDigite o nome da empresa (${org.name}) para confirmar:`);
    if (typed?.trim() !== org.name) return;
    void run(async () => {
      await api.delete(`/api/orgs/${org.id}`);
      await onDeleted();
    }, 'Empresa excluída.');
  }

  const candidates = members.data?.filter((m) => m.role !== 'owner') ?? [];

  return (
    <>
      {error && <p className={styles.error}>{error}</p>}
      {success && <p className={styles.success}>{success}</p>}

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Nome da empresa</h2>
        <form className={styles.inline} onSubmit={rename}>
          <input className={styles.input} maxLength={80} required value={name} onChange={(e) => setName(e.target.value)} />
          <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={!name.trim() || name.trim() === org.name}>
            Salvar
          </button>
        </form>
      </section>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Acesso externo</h2>
        <p className={styles.help}>
          Quem não é colaborador, ou não tem permissão para o computador, ainda pode pedir conexão pelo ID. Só entra se
          alguém aceitar no próprio computador ou se digitar a senha de acesso configurada nele. Essas sessões aparecem
          no histórico como externas. O acesso sem supervisão continua exclusivo dos colaboradores. Se o computador
          também for gerenciado por outra empresa, o acesso externo só vale quando todas permitem.
        </p>
        <label className={styles.check}>
          <input
            type="checkbox"
            checked={org.allowExternalAccess}
            onChange={(e) => setExternalAccess(e.target.checked)}
          />
          Permitir acesso externo aos computadores da empresa
        </label>
      </section>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Transferir propriedade</h2>
        <p className={styles.help}>Só existe um proprietário por empresa. Ao transferir, você passa a ser administrador.</p>
        {candidates.length === 0 ? (
          <p className={styles.empty}>Convide alguém antes de transferir a empresa.</p>
        ) : (
          <form className={styles.inline} onSubmit={transfer}>
            <select className={styles.select} value={newOwner} onChange={(e) => setNewOwner(e.target.value)} required>
              <option value="">Escolha o novo proprietário</option>
              {candidates.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.name} ({m.email})
                </option>
              ))}
            </select>
            <button type="submit" className={`${styles.btn} ${styles.btnSecondary}`} disabled={!newOwner}>
              Transferir
            </button>
          </form>
        )}
      </section>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Excluir empresa</h2>
        <p className={styles.help}>
          Remove a empresa e todo o histórico de permissões. Os computadores não são apagados: continuam nas outras
          empresas que os gerenciam ou voltam ao modo avulso, com ID e aceite manual.
        </p>
        <button type="button" className={`${styles.btn} ${styles.btnDanger}`} onClick={remove}>
          Excluir empresa
        </button>
      </section>
    </>
  );
}
